import { useState } from "react";
import { Check, Clock, Trophy, X } from "lucide-react";
import { AnswerShape, ANSWER_COLORS } from "@/components/game/AnswerShape";
import { PixelSprite } from "@/components/game/PixelSprite";
import { CUSTOMERS, salesmanSprite } from "@/lib/customers";

export const Center = ({ children, testId, top }) => (
  <div data-testid={testId} className={`flex-1 flex flex-col items-center ${top ? "justify-start pt-6" : "justify-center"} text-center gap-5 px-4`}>{children}</div>
);

export const CRTFrame = ({ children, className = "", testId }) => (
  <div className={`crt-bezel ${className}`} data-testid={testId}>
    <div className="crt-screen">
      {children}
      <div className="crt-scanlines" />
      <div className="crt-glare" />
    </div>
    <div className="crt-led" />
    <div className="crt-label">SAMQUEST TV</div>
  </div>
);

// Zoomed head/torso portrait: a big sprite cropped by the overflow-hidden screen.
export const SalesmanPortrait = ({ shirt }) => (
  <div className="relative w-[58vw] max-w-[220px]" style={{ height: "clamp(150px, 42vw, 196px)" }}>
    <PixelSprite params={salesmanSprite(shirt)} height="430px" className="absolute left-1/2" style={{ transform: "translateX(-50%)", top: "-16px" }} />
  </div>
);

export const Big = ({ children }) => <div className="font-press text-xl leading-relaxed" style={{ textShadow: "3px 3px 0 #000" }}>{children}</div>;

export const AnswerGrid = ({ st, onAnswer }) => {
  const n = st.options_count;
  return (
    <div className={`flex-1 grid gap-3 p-3 auto-rows-fr ${n <= 2 ? "grid-cols-1" : "grid-cols-2"}`} data-testid="player-answer-grid">
      {Array.from({ length: n }).map((_, i) => {
        const txt = st.options?.[i];
        return (
          <button
            key={i}
            data-testid={`player-answer-button-${i + 1}`}
            className={`answer-btn flex-col justify-center text-center ${n > 2 && n % 2 === 1 && i === n - 1 ? "col-span-2" : ""}`}
            style={{ background: ANSWER_COLORS[i] }}
            onClick={() => onAnswer(i)}
          >
            <AnswerShape i={i} size={txt ? "34px" : "64px"} />
            {txt && <span className="w-full text-center font-extrabold text-lg sm:text-xl leading-tight [overflow-wrap:anywhere] [hyphens:auto]">{txt}</span>}
          </button>
        );
      })}
    </div>
  );
};

export const RevealView = ({ st, t }) => {
  const unanswered = !st.answered;
  const bg = unanswered ? "#475569" : st.correct ? "#26890C" : "#C01532";
  const Icon = unanswered ? Clock : st.correct ? Check : X;
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center gap-5 px-4" style={{ background: bg }} data-testid="player-reveal-view">
      <div className="bg-white rounded-full p-4 pop-in"><Icon className="w-14 h-14" style={{ color: bg }} strokeWidth={4} /></div>
      <Big><span data-testid="player-result-title">{unanswered ? t("times_up") : st.correct ? t("correct") : t("wrong")}</span></Big>
      {st.correct && <div className="font-num text-4xl bg-black/30 px-4 py-2 rounded leading-none" data-testid="player-result-points">+{st.points}</div>}
      {st.streak > 1 && <div className="font-silk text-sm bg-black/25 px-3 py-1 rounded">{t("streak", { n: st.streak })}</div>}
      <RankLine st={st} t={t} />
    </div>
  );
};

export const RankLine = ({ st, t }) => (
  <div className="font-pixel text-lg" data-testid="player-rank-line">
    {st.rank === 1 ? t("you_lead") : st.rank ? <>{t("place", { n: st.rank })}<br /><span className="text-base opacity-80">{t("behind", { p: st.behind_by, name: st.behind_name })}</span></> : null}
  </div>
);

export const LockedView = ({ option, t, canChange, onChange }) => (
  <Center testId="player-locked-view">
    {option !== null && option !== undefined && (
      <div className="w-24 h-24 flex items-center justify-center rounded-lg pop-in" style={{ background: ANSWER_COLORS[option] }}><AnswerShape i={option} size="56px" /></div>
    )}
    <Big>{t("answer_locked")}</Big>
    <div className="font-pixel text-slate-300 blink">{t("wait_others")}</div>
    {canChange && onChange && (
      <button
        data-testid="player-change-answer-button"
        onClick={onChange}
        className="mt-8 text-xs text-slate-500 underline underline-offset-4 decoration-slate-600 hover:text-slate-300 transition-colors"
      >
        {t("change_answer")}
      </button>
    )}
  </Center>
);

export const IntroView = ({ st, t }) => (
  <Center testId="player-intro-view">
    <CRTFrame testId="player-crt-scene" className="w-[86vw] max-w-[330px]">
      <div className="flex items-end justify-center gap-3 w-full px-4" style={{ height: "clamp(150px, 44vw, 190px)" }}>
        <PixelSprite params={salesmanSprite(st.shirt_color)} height="150px" />
        {st.customer_type && <PixelSprite params={CUSTOMERS[st.customer_type].sprite} height="150px" facing="left" walking />}
      </div>
    </CRTFrame>
    <Big>{t("customer_nr", { n: st.customer_number })}</Big>
    <div className="font-pixel text-xl">{st.customer_name}</div>
    <div className="font-silk text-sm text-slate-400">{t("look_screen")}</div>
  </Center>
);

const ContactForm = ({ st, t, onSubmit }) => {
  const [name, setName] = useState("");
  const [store, setStore] = useState("");
  const [busy, setBusy] = useState(false);
  const inputCls = "w-full max-w-xs px-4 py-3 rounded-lg bg-[#fffbe8] text-[#1a1420] placeholder:text-slate-500 border-4 border-[#1a1420] focus:border-[#FFD700] font-pixel text-xl text-center outline-none";
  const send = async () => {
    if (!name.trim() || !store.trim() || busy) return;
    setBusy(true);
    try { await onSubmit({ name: name.trim(), store: store.trim() }); }
    finally { setBusy(false); }
  };
  return (
    <Center testId="player-contact-view" top>
      {st.rank >= 1 && st.rank <= 3
        ? <img src={`/assets/trophy_${st.rank}.png`} alt={`#${st.rank}`} data-testid="contact-trophy" className="pixelated w-24 h-24 object-contain float-y" />
        : <Trophy className="w-14 h-14 text-[#FFD700] float-y" />}
      <div className="font-pixel text-3xl text-slate-100 px-2 text-center" data-testid="contact-prompt">{t("winner_contact_title")}</div>
      <input data-testid="contact-name-input" className={inputCls} placeholder={t("contact_name_ph")} maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
      <input data-testid="contact-store-input" className={inputCls} placeholder={t("contact_store_ph")} maxLength={80} value={store} onChange={(e) => setStore(e.target.value)} />
      <button data-testid="contact-submit-button" className="px-btn text-sm" disabled={!name.trim() || !store.trim() || busy} onClick={send}>{t("submit_contact")}</button>
    </Center>
  );
};

export const FinalView = ({ st, t, onAgain, onSubmitContact }) => {
  const [justSubmitted, setJustSubmitted] = useState(false);
  const submitted = st.contact_submitted || justSubmitted;
  const submit = async (data) => { await onSubmitContact(data); setJustSubmitted(true); };
  if (st.collect_contact && !submitted) return <ContactForm st={st} t={t} onSubmit={submit} />;
  return (
    <Center testId="player-final-view" top>
      {st.rank >= 1 && st.rank <= 3
        ? <img src={`/assets/trophy_${st.rank}.png`} alt={`#${st.rank}`} data-testid="player-final-trophy" className="pixelated w-28 h-28 object-contain float-y" />
        : <Trophy className="w-16 h-16 text-[#FFD700] float-y" />}
      <Big>{t("game_over")}</Big>
      <div className="font-pixel text-2xl" data-testid="player-final-place">{t("finished_place", { n: st.rank, t: st.player_count })}</div>
      <div className="text-[#FFD700] flex items-baseline gap-2"><span className="font-num text-4xl leading-none">{st.score}</span><span className="font-press text-sm">{t("pts")}</span></div>
      {st.collect_contact && submitted && <div className="font-pixel text-emerald-300 text-base px-2" data-testid="player-contact-thanks">{t("contact_thanks")}</div>}
      <button data-testid="player-play-again-button" className="px-btn text-xs mt-4" onClick={onAgain}>{t("play_again")}</button>
    </Center>
  );
};
